"use client";

import React, { useCallback, useState, useEffect, useMemo } from "react";
import { useReactiveSearchParams } from "@/lib/Hooks/useReactiveSearchParams";
import styled from "styled-components";
import { Checkbox } from "@lmn/design-system";
import IconEco from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconEco.js";
import IconHalfSun from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconHalfSun.js";
import IconSandyBeach from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconSandyBeach.js";
import IconWifi from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-solid/IconWifi.js";
import IconAdultsOnly from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-linear/IconAdultsOnly.js";
import IconSwimmingpool from "@lmn/lastminute-react-icons/icons-converted/lastminute-icons-linear/IconSwimmingpool.js";
import initialState from "@/lib/StaticData/searchFormData";
import { useFilterState } from "@/lib/Providers/FilterStateProvider/FilterStateProvider";
import { HotelCriteriaIds } from "@/lib/Types/searchForm";
import { getParticipants } from "@/components/Stateful/CSW/utils/formatters";

const hotelCriteriaIcons = (criteria: string) => {
  const iconMap: { [key: string]: React.ReactNode } = {
    adultsOnly: <IconAdultsOnly size={24} />,
    beach: <IconHalfSun size={24} />,
    sandyBeach: <IconSandyBeach size={24} />,
    pool: <IconSwimmingpool size={24} />,
    wlan: <IconWifi size={24} />,
    IconEco: <IconEco size={24} />,
  };
  return iconMap[criteria];
};

const FilterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 1rem;
`;

const FilterContent = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  position: relative;
`;

const ClickOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  cursor: pointer;
  z-index: 1;
`;

interface HotelCriteriaFilterProps {
  delayedUpdate?: boolean;
}

const HotelCriteriaFilter: React.FC<HotelCriteriaFilterProps> = ({
  delayedUpdate = false,
}) => {
  const searchParams = useReactiveSearchParams();
  const { tempFilters, updateFilter, updateTempFilterArray } = useFilterState();
  const hotelCriteria = initialState.hotelCriteria;

  const [selectedCategories, setSelectedCategories] = useState<number[]>(() => {
    return delayedUpdate
      ? tempFilters.get("categories")?.split(",").map(Number) ?? []
      : searchParams.get("categories")?.split(",").map(Number) ?? [];
  });

  const hasChildren = useMemo(() => {
    const participants = getParticipants(searchParams);
    return participants.rooms.some((room: any) => room.children?.length > 0);
  }, [searchParams]);

  useEffect(() => {
    const categories =
      searchParams.get("categories")?.split(",").map(Number) ?? [];

    // If there are children, remove category 44 from selected categories and URL
    if (hasChildren && categories.includes(44)) {
      const filteredCategories = categories.filter((id) => id !== 44);
      setSelectedCategories(filteredCategories);

      if (!delayedUpdate) {
        if (filteredCategories.length > 0) {
          updateFilter("categories", filteredCategories.map(String).join(","));
        } else {
          updateFilter("categories", null);
        }
      }
    } else {
      setSelectedCategories(categories);
    }
  }, [searchParams, hasChildren, delayedUpdate, updateFilter]);

  const handleCheckboxChange = useCallback(
    (id: HotelCriteriaIds) => {
      const isCurrentlyChecked = selectedCategories.includes(id);
      const newCategories = isCurrentlyChecked
        ? selectedCategories.filter((catId) => catId !== id)
        : [...selectedCategories, id];

      setSelectedCategories(newCategories);

      if (delayedUpdate) {
        // Just update temp filters, will be committed on dialog confirmation
        updateTempFilterArray("categories", newCategories.map(String));
      } else {
        // Immediate URL update
        const categoryStrings = newCategories.map(String);
        if (categoryStrings.length > 0) {
          updateFilter("categories", categoryStrings.join(","));
        } else {
          updateFilter("categories", null);
        }
      }
    },
    [selectedCategories, updateFilter, delayedUpdate, updateTempFilterArray]
  );

  return (
    <FilterWrapper data-testid="hotelCriteriaFilter">
      {hotelCriteria.allIds
        .filter((id) => id !== 101 && !(hasChildren && id === 44))
        .map((id) => {
          const criteria = hotelCriteria.byId[id];
          const isChecked = selectedCategories.includes(id);

          return (
            <FilterContent key={id}>
              {delayedUpdate && (
                <ClickOverlay onClick={() => handleCheckboxChange(id)} />
              )}
              {hotelCriteriaIcons(criteria.icon)}
              <Checkbox
                checkbox={{
                  id: id.toString(),
                  label: criteria.label,
                }}
                isChecked={isChecked}
                onChange={
                  delayedUpdate ? () => { } : () => handleCheckboxChange(id)
                }
              />
            </FilterContent>
          );
        })}
    </FilterWrapper>
  );
};

export default HotelCriteriaFilter;
